import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { http } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { Share2, Plus, Search, MoreHorizontal, ChevronDown, ChevronUp, Inbox, Send, Users, Building2, UserRound } from "lucide-react";
import { toTitleCasePt } from "@/lib/format";
import { useAutoRefresh } from "@/hooks/useAutoRefresh";
import { useAuth } from "@/context/AuthContext";
import { Dica } from "@/components/Dica";
import SharedInbox from "@/components/SharedInbox";
import ColabContatos from "@/components/colab/ColabContatos";
import ColabWizard from "@/components/colab/ColabWizard";
import { EstadoTag, AtoTag, ESTADOS_FILTRO, ItemEvento, ItemProcesso, NovidadeTag, ACOES_HISTORICO, dataBR, dataHoraBR } from "@/components/colab/colabUi";
import ColabInteracao from "@/components/colab/ColabInteracao";
import { CartaoRecebido } from "@/components/colab/ColabRecebidos";

// Lote 160 — Compartilhamentos (substitui "Cadastro de equipe"): colaboração com parceiros externos.
// Três abas: Enviados | Recebidos | Contatos. O parceiro só vê o que foi concedido, enquanto o acesso estiver ativo.
const ABAS = [["enviados", "Enviados", Send], ["recebidos", "Recebidos", Inbox], ["contatos", "Contatos", Users]];
const detalheDe = (e) => e?.response?.data?.detail;
const msgErro = (e, padrao) => (typeof detalheDe(e) === "string" ? detalheDe(e) : padrao);
const VIVOS = ["aguardando_aceite", "ativo"];

function Validade({ s }) {
  if (s.estado === "aguardando_aceite") return <span>Convite vale até {dataBR(s.convite_expira_em)}</span>;
  if (s.estado === "ativo") return <span>Acesso até {dataBR(s.acesso_ate)}</span>;
  if (s.estado === "acesso_expirado") return <span>Expirou em {dataBR(s.acesso_ate)}</span>;
  if (s.estado === "convite_expirado") return <span>Convite expirou em {dataBR(s.convite_expira_em)}</span>;
  if (s.estado === "encerrado") return <span>Encerrado em {dataBR(s.encerrado_em)}</span>;
  return null;
}

// ---------------------------------------------------------------- Enviados
function PainelAtoDono({ det, onMudou }) {
  const [nota, setNota] = useState("");
  const [busy, setBusy] = useState(false);
  const a = det.ato || {};
  const conferir = async (ok) => {
    setBusy(true);
    try { await http.post(`/colab/compartilhamentos/${det.share_id}/conferir`, { ok, nota }); toast.success(ok ? "Realização conferida" : "Devolvido ao parceiro"); setNota(""); onMudou(); }
    catch (e) { toast.error(msgErro(e, "Não foi possível conferir")); } finally { setBusy(false); }
  };
  return (
    <div data-testid={`env-ato-${det.share_id}-painel`} className="rounded-lg border border-stone-200 bg-stone-50 p-3 space-y-2">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[11px] font-bold uppercase tracking-widest text-stone-500">Ato solicitado</span>
        <AtoTag estado={det.ato_estado} rotulo={det.ato_rotulo} />
      </div>
      <p className="text-sm text-stone-900">{det.instrucao}</p>
      {det.ato_estado === "reconfirmar" && <p className="text-xs text-amber-900">Os eventos mudaram depois do aceite: o parceiro precisa confirmar de novo.</p>}
      {det.ato_estado === "recusado" && <p className="text-xs text-stone-700">Recusado{a.motivo ? `: ${a.motivo}` : ""}. Crie um novo compartilhamento se quiser pedir a outra pessoa.</p>}
      {a.realizacao && (
        <p className="text-sm" data-testid={`env-realizacao-${det.share_id}`}>Parceiro informou: <b>{a.realizacao.rotulo}</b>{a.realizacao.observacao ? ` — ${a.realizacao.observacao}` : ""} <span className="text-xs text-stone-500">({dataHoraBR(a.realizacao.quando)})</span></p>
      )}
      {det.ato_estado === "realizacao_informada" && (
        <div className="space-y-2">
          <Input data-testid={`env-conferir-nota-${det.share_id}`} value={nota} onChange={(e) => setNota(e.target.value)} placeholder="Nota ao parceiro (obrigatória se não confere)" maxLength={1000} />
          <div className="flex gap-2 flex-wrap">
            <Button size="sm" data-testid={`env-conferir-ok-${det.share_id}`} disabled={busy} onClick={() => conferir(true)} className="bg-stone-900 hover:bg-stone-800 text-white">Conferir realização</Button>
            <Button size="sm" variant="outline" data-testid={`env-conferir-nao-${det.share_id}`} disabled={busy} onClick={() => conferir(false)}>Não confere — devolver</Button>
          </div>
        </div>
      )}
      {det.ato_estado === "conferido" && <p className="text-xs text-emerald-900">Conferido{a.conferencia?.quando ? ` em ${dataBR(a.conferencia.quando)}` : ""}.</p>}
    </div>
  );
}

function CartaoEnviado({ s, aberto, onAlternar, onEncerrar, mostrarDono, onAtualizar }) {
  const [det, setDet] = useState(null);
  useEffect(() => {
    if (!aberto) return;
    http.get(`/colab/compartilhamentos/${s.share_id}`).then((r) => setDet(r.data)).catch(() => setDet({ erro: true }));
  }, [aberto, s.share_id, s.estado]);
  const vivo = VIVOS.includes(s.estado);
  const ehProc = s.objeto === "processo";
  const nomeItem = (n) => (ehProc ? (n === 1 ? "processo" : "processos") : (n === 1 ? "evento" : "eventos"));
  return (
    <li data-testid={`env-${s.share_id}`} className="bg-white border border-stone-200 rounded-md">
      <div className="flex items-start gap-2 px-4 py-3">
        <button type="button" data-testid={`env-abrir-${s.share_id}`} onClick={onAlternar} aria-expanded={aberto} className="min-w-0 flex-1 text-left">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-display text-base font-semibold text-stone-900 leading-snug">{toTitleCasePt(s.destinatario_nome || s.destinatario_email || "—")}</span>
            <EstadoTag estado={s.estado} rotulo={s.estado_rotulo} testid={`env-estado-${s.share_id}`} />
            {s.ato_estado && <AtoTag estado={s.ato_estado} rotulo={s.ato_rotulo} testid={`env-ato-${s.share_id}`} />}
            <NovidadeTag n={s.novidades} testid={`env-novidades-${s.share_id}`} />
          </div>
          <div className="text-xs text-stone-500 mt-0.5">
            {s.n_itens} {nomeItem(s.n_itens)} · {s.finalidade_rotulo} · <Validade s={s} />
            {mostrarDono && s.dono_nome ? <> · conta de {s.dono_nome}</> : null}
          </div>
        </button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button type="button" variant="ghost" size="sm" aria-label="Mais ações" data-testid={`env-menu-${s.share_id}`} className="text-stone-500 h-8 w-8 p-0">
              <MoreHorizontal className="w-4 h-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem data-testid={`env-ver-${s.share_id}`} onSelect={onAlternar}>{aberto ? "Recolher detalhes" : "Ver detalhes"}</DropdownMenuItem>
            {vivo && <DropdownMenuItem data-testid={`env-encerrar-${s.share_id}`} onSelect={() => onEncerrar(s)} className="text-rose-700">Encerrar acesso</DropdownMenuItem>}
          </DropdownMenuContent>
        </DropdownMenu>
        <button type="button" aria-label={aberto ? "Recolher" : "Expandir"} onClick={onAlternar} className="h-8 w-8 inline-flex items-center justify-center text-stone-400">
          {aberto ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
      </div>
      {aberto && (
        <div data-testid={`env-det-${s.share_id}`} className="border-t border-stone-100 px-4 py-3 space-y-3">
          {!det ? <div className="text-sm text-stone-500">Carregando…</div> : det.erro ? (
            <div className="text-sm text-rose-800">Não foi possível carregar os detalhes. Tente de novo em instantes.</div>
          ) : (
            <>
              <div className="text-xs text-stone-600">
                Para <b>{det.destinatario_email || "—"}</b>{det.org_nome ? <> · em nome de {det.org_nome}</> : null}
                {det.mensagem ? <div className="mt-1 italic">“{det.mensagem}”</div> : null}
              </div>
              {det.finalidade === "ato" && <PainelAtoDono det={det} onMudou={() => { setDet(null); onAtualizar(); http.get(`/colab/compartilhamentos/${s.share_id}`).then((r) => setDet(r.data)).catch(() => setDet({ erro: true })); }} />}
              <div>
                <div className="text-[11px] font-bold uppercase tracking-widest text-stone-500 mb-1">O que o parceiro pode ver</div>
                {ehProc && <p data-testid={`env-categorias-${s.share_id}`} className="text-xs text-stone-700 mb-1.5">Categorias liberadas: <b>{(det.categorias_rotulos || []).join(", ")}</b></p>}
                <ul className="space-y-2">
                  {det.itens.map((it) => <li key={it.ref}>{ehProc ? <ItemProcesso it={{ ...it, eventos: undefined, andamentos: undefined }} testid={`env-item-${s.share_id}-${it.ref}`} /> : <ItemEvento it={it} detalhado testid={`env-item-${s.share_id}-${it.ref}`} />}</li>)}
                </ul>
                <p className="text-[11px] text-stone-500 mt-2">{ehProc
                  ? "O parceiro não altera nada sozinho: ele comenta e propõe, e só você decide. Não vê cliente, apelido, anotações internas, valores nem documentos."
                  : "Somente consulta: o parceiro não altera nem apaga nada, e não vê cliente, anotações internas nem dados de sala virtual."}</p>
              </div>
              {ehProc && det.estado !== "aguardando_aceite" && (
                <ColabInteracao sid={s.share_id} base={`/colab/compartilhamentos/${s.share_id}`} lado="dono" onMudou={onAtualizar} />
              )}
              <div>
                <div className="text-[11px] font-bold uppercase tracking-widest text-stone-500 mb-1">Histórico</div>
                <ul data-testid={`env-hist-${s.share_id}`} className="space-y-0.5 text-xs text-stone-600">
                  {(det.historico || []).map((h, i) => (
                    <li key={i}><span className="tabular-nums text-stone-500">{dataHoraBR(h.quando)}</span> — {ACOES_HISTORICO[h.acao] || h.acao}{h.autor_nome ? ` (${h.autor_nome})` : ""}</li>
                  ))}
                </ul>
              </div>
              {vivo && (
                <Button type="button" variant="outline" size="sm" data-testid={`env-encerrar-btn-${s.share_id}`} onClick={() => onEncerrar(s)} className="border-rose-200 text-rose-700 hover:bg-rose-50">
                  Encerrar acesso
                </Button>
              )}
            </>
          )}
        </div>
      )}
    </li>
  );
}

function AbaEnviados({ ehAdmin, recarregarKey, abrirId }) {
  const [escopo, setEscopo] = useState("meus");
  const [q, setQ] = useState("");
  const [objeto, setObjeto] = useState("");
  const [situacao, setSituacao] = useState("");
  const [lista, setLista] = useState(null);
  const [erro, setErro] = useState(false);
  const [copias, setCopias] = useState([]);
  const [antigos, setAntigos] = useState([]);
  const [aberto, setAberto] = useState(abrirId || null);
  const [confirma, setConfirma] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const params = { escopo };
      if (q.trim()) params.q = q.trim();
      if (objeto) params.objeto = objeto;
      if (situacao) params.situacao = situacao;
      setLista((await http.get("/colab/compartilhamentos", { params })).data); setErro(false);
    } catch { setErro(true); }
  }, [escopo, q, objeto, situacao]);
  useEffect(() => { const t = setTimeout(load, q ? 250 : 0); return () => clearTimeout(t); }, [load, recarregarKey]); // eslint-disable-line react-hooks/exhaustive-deps
  useAutoRefresh(load);
  useEffect(() => {
    http.get("/colab/copias").then((r) => setCopias(r.data)).catch(() => {});
    http.get("/compartilhamentos/enviados").then((r) => setAntigos(r.data)).catch(() => {});
  }, [recarregarKey]);

  const encerrar = async () => {
    setBusy(true);
    try {
      await http.post(`/colab/compartilhamentos/${confirma.share_id}/encerrar`);
      toast.success("Acesso encerrado — o parceiro não consegue mais abrir o que foi compartilhado");
      setConfirma(null); await load();
    } catch (e) { toast.error(msgErro(e, "Não consegui encerrar")); } finally { setBusy(false); }
  };
  const filtrando = !!(q.trim() || objeto || situacao);
  const cls = "h-9 rounded-md border border-stone-300 bg-white px-2 text-sm text-stone-700";

  return (
    <div data-testid="aba-enviados">
      {ehAdmin && (
        <div className="inline-flex rounded-md bg-stone-100 p-1 mb-3" role="group" aria-label="Quais compartilhamentos mostrar">
          {[["meus", "Os meus", UserRound], ["escritorio", "Do escritório", Building2]].map(([v, l, Ic]) => (
            <button key={v} type="button" data-testid={`escopo-${v}`} onClick={() => setEscopo(v)} aria-pressed={escopo === v}
              className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded ${escopo === v ? "bg-white shadow-sm text-stone-900" : "text-stone-500"}`}>
              <Ic className="w-3.5 h-3.5" strokeWidth={1.75} />{l}
            </button>
          ))}
        </div>
      )}
      <div className="flex flex-wrap gap-2 mb-3">
        <div className="relative flex-1 min-w-[180px]">
          <Search className="w-4 h-4 text-stone-400 absolute left-2.5 top-2.5" strokeWidth={1.75} />
          <Input data-testid="env-busca" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar parceiro, partes ou processo" className="pl-8 h-9" />
        </div>
        <select data-testid="env-objeto" aria-label="Tipo de item" value={objeto} onChange={(e) => setObjeto(e.target.value)} className={cls}>
          <option value="">Todos os itens</option><option value="evento">Eventos</option><option value="processo">Processos</option>
        </select>
        <select data-testid="env-situacao" aria-label="Situação" value={situacao} onChange={(e) => setSituacao(e.target.value)} className={cls}>
          {ESTADOS_FILTRO.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </div>

      {erro ? (
        <div data-testid="env-erro" className="rounded-md border border-rose-200 bg-rose-50 p-4 text-sm text-rose-900">
          Não foi possível carregar os compartilhamentos. <button type="button" className="underline font-semibold" onClick={load}>Tentar de novo</button>
        </div>
      ) : lista === null ? (
        <div className="p-8 text-center text-stone-500">Carregando…</div>
      ) : lista.length === 0 ? (
        <div data-testid={filtrando ? "env-sem-resultado" : "env-vazio"} className="bg-white border border-stone-200 rounded-md p-10 text-center">
          <Share2 className="w-10 h-10 mx-auto text-stone-300 mb-3" strokeWidth={1.25} />
          <p className="font-display text-lg text-stone-900 mb-1">{filtrando ? "Nada encontrado com esses filtros" : "Você ainda não compartilhou nada"}</p>
          <p className="text-sm text-stone-500">{filtrando ? "Limpe a busca ou troque a situação." : "Toque em “Compartilhar” para dar a um parceiro acesso a eventos ou ao acompanhamento de processos."}</p>
        </div>
      ) : (
        <ul className="space-y-2.5">
          {lista.map((s) => (
            <CartaoEnviado key={s.share_id} s={s} mostrarDono={escopo === "escritorio"} aberto={aberto === s.share_id}
              onAlternar={() => setAberto(aberto === s.share_id ? null : s.share_id)} onEncerrar={setConfirma} onAtualizar={load} />
          ))}
        </ul>
      )}

      {copias.length > 0 && (
        <section className="mt-6" data-testid="env-copias">
          <h2 className="text-xs font-bold uppercase tracking-[0.2em] mb-1 text-stone-500">Cópias enviadas</h2>
          <p className="text-[11px] text-stone-500 mb-2">Uma cópia é um resumo fixo: não acompanha alterações e não dá acesso ao IApauta. Encerrar um acesso não apaga cópias já enviadas.</p>
          <ul className="space-y-1.5">
            {copias.slice(0, 20).map((c) => (
              <li key={c.copia_id} className="bg-white border border-stone-200 rounded-md px-3 py-2 text-sm">
                <b>{toTitleCasePt(c.destinatario_nome || "—")}</b> · {c.itens} {c.tipo === "processo" ? (c.itens === 1 ? "processo" : "processos") : (c.itens === 1 ? "evento" : "eventos")} · {dataHoraBR(c.quando)}
                <span className="text-xs text-stone-500"> · {{ gerado: "texto gerado", preparado: "mensagem preparada no WhatsApp", enviado_ao_servico: "e-mail entregue ao serviço de envio", falhou: "falha no envio" }[c.situacao] || c.situacao}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {antigos.length > 0 && (
        <section className="mt-6" data-testid="team-sent-list">
          <h2 className="text-xs font-bold uppercase tracking-[0.2em] mb-1 text-stone-500">Compartilhamentos antigos (acompanham suas mudanças)</h2>
          <p className="text-[11px] text-stone-500 mb-2">Enviados antes desta versão. Continuam funcionando como antes e deixam de ser criados.</p>
          <ul className="space-y-1.5">
            {antigos.slice(0, 15).map((it) => {
              const h = it.hearing || {};
              const rot = { pending: "Aguardando aceite", accepted: "Aceito — acompanha suas mudanças", declined: "Recusado", cancelled: "Cancelado", unlinked: "Desvinculado" }[it.status] || it.status;
              return (
                <li key={it.share_id} data-testid={`sent-${it.share_id}`} className="bg-white border border-stone-200 rounded-md px-3 py-2.5 flex items-center gap-2">
                  <div className="min-w-0 flex-1 text-sm">
                    <div className="truncate"><strong>{toTitleCasePt(it.to_name || "")}</strong> · {dataBR(h.date)}{h.time ? ` ${h.time}` : ""} · {h.case_number || h.description || "—"}</div>
                    <div className="text-xs text-stone-500">{rot}</div>
                  </div>
                  {(it.status === "pending" || it.status === "accepted") && (
                    <Button variant="ghost" size="sm" className="text-xs text-stone-500 shrink-0" data-testid={`unshare-${it.share_id}`}
                      onClick={async () => {
                        if (!window.confirm(it.status === "pending" ? "Cancelar este convite?" : "Desvincular? O compromisso continua na agenda da pessoa, mas deixa de acompanhar suas mudanças.")) return;
                        try { await http.delete(`/compartilhamentos/${it.share_id}`); const r = await http.get("/compartilhamentos/enviados"); setAntigos(r.data); } catch (e) { toast.error(msgErro(e, "Não consegui desvincular")); }
                      }}>
                      {it.status === "pending" ? "Cancelar" : "Desvincular"}
                    </Button>
                  )}
                </li>
              );
            })}
          </ul>
        </section>
      )}

      <Dialog open={!!confirma} onOpenChange={(o) => !o && setConfirma(null)}>
        <DialogContent data-testid="env-confirma-encerrar">
          <DialogHeader>
            <DialogTitle className="font-display">Encerrar este acesso?</DialogTitle>
            <DialogDescription>
              {confirma ? `${toTitleCasePt(confirma.destinatario_nome || confirma.destinatario_email || "O parceiro")} deixa de ver ${confirma.objeto === "processo" ? (confirma.n_itens === 1 ? "o processo" : `os ${confirma.n_itens} processos`) : `os ${confirma.n_itens} ${confirma.n_itens === 1 ? "evento" : "eventos"}`} na hora. O histórico fica guardado e cópias já enviadas não são apagadas.` : ""}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirma(null)}>Voltar</Button>
            <Button data-testid="env-confirma-encerrar-ok" disabled={busy} onClick={encerrar} className="bg-rose-700 hover:bg-rose-800 text-white">{busy ? "Encerrando…" : "Encerrar acesso"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---------------------------------------------------------------- Recebidos
function AbaRecebidos({ abrirId, onMudou }) {
  const [lista, setLista] = useState(null);
  const [erro, setErro] = useState(false);
  const [aberto, setAberto] = useState(abrirId || null);
  const load = useCallback(async () => { try { setLista((await http.get("/colab/recebidos")).data); setErro(false); } catch { setErro(true); } }, []);
  useEffect(() => { load(); }, [load]);
  useAutoRefresh(load);
  const mudou = async () => { await load(); onMudou?.(); };
  const naoEncontrado = abrirId && lista && !lista.some((x) => x.share_id === abrirId);
  return (
    <div data-testid="aba-recebidos">
      <SharedInbox onChange={load} />
      {naoEncontrado && (
        <div data-testid="rec-nao-encontrado" className="mb-3 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
          Este convite não está disponível para a sua conta. Entre com o mesmo e-mail em que o convite foi enviado (e confirme o e-mail, se a conta usa senha).
        </div>
      )}
      {erro ? (
        <div data-testid="rec-erro" className="rounded-md border border-rose-200 bg-rose-50 p-4 text-sm text-rose-900">
          Não foi possível carregar os convites. <button type="button" className="underline font-semibold" onClick={load}>Tentar de novo</button>
        </div>
      ) : lista === null ? (
        <div className="p-8 text-center text-stone-500">Carregando…</div>
      ) : lista.length === 0 ? (
        <div data-testid="rec-vazio" className="bg-white border border-stone-200 rounded-md p-10 text-center">
          <Inbox className="w-10 h-10 mx-auto text-stone-300 mb-3" strokeWidth={1.25} />
          <p className="font-display text-lg text-stone-900 mb-1">Nada compartilhado com você</p>
          <p className="text-sm text-stone-500">Quando alguém compartilhar eventos ou processos com o seu e-mail, o convite aparece aqui.</p>
        </div>
      ) : (
        <ul className="space-y-2.5">
          {lista.map((r) => <CartaoRecebido key={r.share_id} r={r} base="/colab/recebidos" aberto={aberto === r.share_id} onAlternar={() => setAberto(aberto === r.share_id ? null : r.share_id)} onMudou={mudou} />)}
        </ul>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- página
export default function Compartilhamentos() {
  const { user } = useAuth() || {};
  const [params, setParams] = useSearchParams();
  const abaParam = params.get("aba");
  const [aba, setAba] = useState(ABAS.some((a) => a[0] === abaParam) ? abaParam : "enviados");
  const [wiz, setWiz] = useState(null);                 // null = fechado | { tipo, refs, contatoId }
  const [tick, setTick] = useState(0);
  const abrirId = params.get("abrir");

  useEffect(() => {
    const novo = params.get("novo");
    if (novo === "evento" || novo === "processo") {
      const ref = params.get("ref");
      setWiz({ tipo: novo, refs: ref ? [ref] : [] });
      setAba("enviados");
    } else if (novo === "1") setWiz({ tipo: "evento", refs: [] });
    if (novo) { const p = new URLSearchParams(params); p.delete("novo"); p.delete("ref"); setParams(p, { replace: true }); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (abrirId) setAba("recebidos"); }, [abrirId]);

  const trocar = (a) => { setAba(a); const p = new URLSearchParams(params); p.set("aba", a); p.delete("abrir"); setParams(p, { replace: true }); };
  const ehAdmin = user?.escritorio?.papel === "admin";
  const espaco = useMemo(() => (user?.escritorio ? `Escritório ${user.escritorio.nome}` : "Conta pessoal"), [user]);

  return (
    <div className="p-4 md:p-8 lg:p-12 max-w-4xl mx-auto pb-24 md:pb-8">
      <header className="flex items-start justify-between gap-3 mb-4">
        <div className="min-w-0">
          <div className="text-[11px] font-bold tracking-[0.28em] uppercase text-emerald-brand mb-2">Colaboração</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold text-stone-900">Compartilhamentos</h1>
          <p className="text-stone-500 mt-1.5 text-sm">Compartilhe eventos e processos com parceiros externos.</p>
        </div>
        <Button data-testid="compartilhar-novo" onClick={() => setWiz({ tipo: "evento", refs: [] })} className="bg-stone-800 hover:bg-stone-900 text-white gap-2 shrink-0 mt-1">
          <Plus className="w-4 h-4" strokeWidth={2} /> Compartilhar
        </Button>
      </header>

      <div className="flex items-center gap-2 mb-4 text-xs text-stone-600" data-testid="espaco-ativo">
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-stone-200 bg-white">
          {user?.escritorio ? <Building2 className="w-3.5 h-3.5" strokeWidth={1.75} /> : <UserRound className="w-3.5 h-3.5" strokeWidth={1.75} />}
          Compartilhando a partir de: <b className="font-semibold">{espaco}</b>
        </span>
      </div>
      <Dica className="text-xs text-stone-500 mb-4">{`O parceiro só enxerga o que você escolher, só depois de aceitar o convite com o próprio e-mail, e só enquanto você mantiver o acesso. Você pode encerrar a qualquer momento.`}</Dica>

      <div role="tablist" aria-label="Compartilhamentos" className="flex gap-1 border-b border-stone-200 mb-4">
        {ABAS.map(([v, l, Ic]) => (
          <button key={v} type="button" role="tab" aria-selected={aba === v} data-testid={`tab-${v}`} onClick={() => trocar(v)}
            className={`inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-semibold -mb-px border-b-2 ${aba === v ? "border-stone-900 text-stone-900" : "border-transparent text-stone-500 hover:text-stone-700"}`}>
            <Ic className="w-4 h-4" strokeWidth={1.75} />{l}
          </button>
        ))}
      </div>

      {aba === "enviados" && <AbaEnviados ehAdmin={ehAdmin} recarregarKey={tick} abrirId={null} />}
      {aba === "recebidos" && <AbaRecebidos abrirId={abrirId} onMudou={() => setTick((t) => t + 1)} />}
      {aba === "contatos" && <ColabContatos onCompartilhar={(id) => setWiz({ tipo: "evento", refs: [], contatoId: id })} onMudou={() => setTick((t) => t + 1)} />}

      <ColabWizard open={!!wiz} inicial={wiz || undefined} onClose={() => setWiz(null)} onFeito={() => setTick((t) => t + 1)} />
    </div>
  );
}
